import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, LOCATION_NAMES } from "@/lib/format";
import { PageHeader, Btn, Modal, Input } from "@/components/ui/kit";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCircle } from "lucide-react";

const empty = { name: "", email: "", password: "", role: "cajero", location: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const roles = user?.role === "super-admin"
    ? ["super-admin", "administrador", "supervisor", "cajero"]
    : ["administrador", "supervisor", "cajero"];

  const load = async () => { try { const { data } = await api.get("/users"); setUsers(data); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        const body = { name: editing.name, email: editing.email, role: editing.role, location: editing.location || null, is_active: editing.is_active };
        if (editing.password) body.password = editing.password;
        await api.put(`/users/${editing.id}`, body);
      } else {
        if (!editing.password) { toast.error("La contraseña es obligatoria"); return; }
        await api.post("/users", { ...editing, location: editing.location || null });
      }
      toast.success("Usuario guardado"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (u) => { if (!window.confirm(`¿Eliminar ${u.name}?`)) return; try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <PageHeader title="Usuarios" subtitle="Gestión de cuentas y roles del sistema">
        <Btn onClick={() => setEditing({ ...empty })} data-testid="new-user-btn"><Plus className="w-4 h-4" /> Nuevo Usuario</Btn>
      </PageHeader>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase">
            <tr><th className="text-left p-3">Usuario</th><th className="text-left p-3">Email</th><th className="text-left p-3">Rol</th><th className="text-left p-3">Estado</th><th className="text-right p-3">Acciones</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-accent/30" data-testid={`user-row-${u.id}`}>
                <td className="p-3"><div className="flex items-center gap-2"><UserCircle className="w-6 h-6 text-primary" /><span className="font-medium">{u.name}</span></div></td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full border border-primary/30 text-primary">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="p-3">{u.is_active ? <span className="text-emerald-500 text-xs">Activo</span> : <span className="text-red-500 text-xs">Inactivo</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing({ ...u, password: "" })} data-testid={`edit-user-${u.id}`} className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                    {u.role !== "super-admin" && u.id !== user.id && <button onClick={() => del(u)} data-testid={`del-user-${u.id}`} className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Editar usuario" : "Nuevo usuario"}>
        {editing && (
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Nombre</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-name" /></div>
            <div><label className="text-sm font-medium mb-1 block">Email</label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-email" /></div>
            <div><label className="text-sm font-medium mb-1 block">Contraseña {editing.id && <span className="text-muted-foreground">(dejar vacío para no cambiar)</span>}</label><Input type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="user-password" /></div>
            <div><label className="text-sm font-medium mb-1 block">Rol</label>
              <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="user-role" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none">
                {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Local asignado (opcional)</label>
              <select value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none">
                <option value="">Todos</option>
                {Object.entries(LOCATION_NAMES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {editing.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Usuario activo</label>}
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancelar</Btn>
              <Btn onClick={save} data-testid="save-user" className="flex-1">Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
